/**
 * PartsOrdersCard — Shows parts order status for a work order.
 *
 * Displays ordered parts grouped by PO, with supplier, status, ETA.
 * Includes "Order Parts" button to open OrderPartsDialog.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useWorkOrderPartsStatus } from '@/hooks/useWorkOrders';
import { OrderPartsDialog } from './OrderPartsDialog';
import { formatDate } from '@/utils/locale';

interface PartsOrdersCardProps {
  workOrderId: string;
  cardVariant?: 'elevation' | 'outlined';
}

export function PartsOrdersCard({ workOrderId, cardVariant = 'elevation' }: PartsOrdersCardProps) {
  const { t } = useTranslation('workorders');
  const { data: partsStatus, isLoading } = useWorkOrderPartsStatus(workOrderId);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  if (isLoading || !partsStatus) return null;

  const hasOnOrderParts = partsStatus.onOrderParts.length > 0;
  const hasNeededParts = partsStatus.neededParts.length > 0;

  // If no needed parts and no on-order parts, don't render
  if (!hasNeededParts && !hasOnOrderParts) return null;

  // Group on-order parts by PO
  const poGroups = new Map<string, typeof partsStatus.onOrderParts>();
  for (const part of partsStatus.onOrderParts) {
    const group = poGroups.get(part.poId) ?? [];
    group.push(part);
    poGroups.set(part.poId, group);
  }

  const poStatusColor: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
    draft: 'default',
    pending_approval: 'warning',
    approved: 'info',
    ordered: 'info',
    sent_to_supplier: 'info',
    confirmed: 'info',
    partially_received: 'warning',
    received: 'success',
    cancelled: 'default',
  };

  /**
   * Finnish labels for the purchase-order status (#2720).
   *
   * The chip used to render `poStatus.replace(/_/g, ' ')`, so a Finnish
   * mechanic read "partially received" in English on the work order. The
   * translations already existed and were used by PurchaseOrdersPage — this
   * component simply did not reach for them.
   *
   * Keys are camelCase while the stored values are snake_case, which is why
   * this is a map rather than an interpolated key. An unknown status falls
   * back to the raw value: better a bare token than an empty chip, and it
   * surfaces a status the schema gained without a label.
   */
  const poStatusLabel: Record<string, string> = {
    draft: t('purchaseOrders.status.draft', { ns: 'inventory' }),
    pending_approval: t('purchaseOrders.status.pendingApproval', { ns: 'inventory' }),
    approved: t('purchaseOrders.status.approved', { ns: 'inventory' }),
    ordered: t('purchaseOrders.status.ordered', { ns: 'inventory' }),
    sent_to_supplier: t('purchaseOrders.status.sentToSupplier', { ns: 'inventory' }),
    confirmed: t('purchaseOrders.status.confirmed', { ns: 'inventory' }),
    partially_received: t('purchaseOrders.status.partiallyReceived', { ns: 'inventory' }),
    received: t('purchaseOrders.status.received', { ns: 'inventory' }),
    cancelled: t('purchaseOrders.status.cancelled', { ns: 'inventory' }),
  };

  return (
    <>
      <Card variant={cardVariant} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon sx={{ fontSize: 20 }} />
              <Typography variant="h6">{t('partsOrders.title')}</Typography>
            </Box>
            {hasNeededParts && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => setOrderDialogOpen(true)}
              >
                {t('partsOrders.orderParts')}
              </Button>
            )}
          </Box>

          {!hasOnOrderParts && (
            <Typography variant="body2" color="text.secondary">
              {t('partsOrders.noOrders')}
            </Typography>
          )}

          {/* PO groups */}
          {[...poGroups.entries()].map(([poId, lines], idx) => {
            const firstLine = lines[0]!;
            const allReceived = lines.every(l => l.quantityReceived >= l.quantityOrdered);

            return (
              <Box key={poId}>
                {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {firstLine.poOrderNumber}
                    </Typography>
                    <Chip
                      label={poStatusLabel[firstLine.poStatus] ?? firstLine.poStatus}
                      size="small"
                      color={poStatusColor[firstLine.poStatus] ?? 'default'}
                    />
                  </Box>
                  {allReceived && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('partsOrders.supplier')}: {firstLine.supplierName}
                  {firstLine.expectedDeliveryDate && ` — ${formatDate(firstLine.expectedDeliveryDate)}`}
                </Typography>

                {lines.map((line) => {
                  const progress = line.quantityOrdered > 0
                    ? (line.quantityReceived / line.quantityOrdered) * 100
                    : 0;
                  return (
                    <Box key={line.lineId} sx={{ mb: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">{line.partName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {line.quantityReceived}/{line.quantityOrdered}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        color={progress >= 100 ? 'success' : 'primary'}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </CardContent>
      </Card>

      <OrderPartsDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        workOrderId={workOrderId}
        neededParts={partsStatus.neededParts}
      />
    </>
  );
}
